import {
  cruiseOfferTileClick,
  offerClicked,
} from 'analytics/eventDefinitions'
import { addGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import CurrencyContext from 'contexts/currencyContext'
import useOffer from 'hooks/Offers/useOffer'
import { isCruiseOffer, isHotel, isLEOffer } from 'lib/offer/offerTypes'
import type { RefCallback } from 'react'
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import FrontPageTile from './FrontPageOfferTiles/FrontPageTile'
import OfferSearchTile from './SearchOfferTiles/OfferSearchTile'
import type { OfferListEventHandler } from '../OfferListEventsContext'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from '../OfferListEventsContext'
import OfferListLoadingOfferTile from './OfferListLoadingOfferTile'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroOfferTile from './HeroOfferTiles/HeroOfferTile'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import {
  calculateHasBeenInView,
  calculateTrackingInView,
  handleSkipInView,
} from 'components/utils/OfferInView'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { calculateDaysBetweenDates } from 'lib/datetime/dateUtils'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getAccommodationSearchTileView } from 'components/SearchKit/SearchTiles/accommodations/accommodationSearchTileView'
import useSearchPropertyRateLoyaltyPackageUpgrade from 'loyalty/hooks/useSearchPropertyRateLoyaltyPackageUpgrade'
import type { MetaSearchResult } from './OfferTileTypes'

interface Props {
  /**
   * The visual style of tile that should be rendered
   */
  tileStyle: 'search' | 'frontpage' | 'hero'
  /**
   * The offer ID this tile is for
   */
  offerId: string
  /**
   * The filters associated with the list that's rendering this tile
   */
  filters?: App.OfferListFilters
  /** Show Société points pricing instead of cash pricing on tiles that support it */
  viewPriceInPoints?: boolean
  /**
   * The position of this tile in the offer list.
   * Used for analytics
   */
  position: number
  /**
   * Will eager load (not wait till in view) the first image of whatever image carousel is shown for this offer
   * Use this mostly for SSR
   */
  eagerLoadImage?: boolean
  className?: string
  /**
   * Dictate the behaviour of when the tile will mount/load
   *
   * offer-and-inview:
   * Will fetch the offer once in view
   * Will show the tile once both the offer AND the tile is in view
   * This is used to ensure the tile isn't mounted when out of view
   * Note: This will prevent the tile from being SSR'ed
   *
   * offer-or-inview:
   * Will fetch the offer once in view
   * Will show the tile as soon as the offer is available (from any source)
   * Used when you wish the tile to be able to SSR'ed
   *
   * none:
   * Will fetch the offer straight
   * Will show the tile as soon as the offer is ready
   *
   * @default none
   */
  lazyLoad?: 'offer-and-inview' | 'offer-or-inview' | 'none'
  wrapperClass?: string
  metaSearchResult?: MetaSearchResult
  accommodationId?: string
  showPerNightPricing?: boolean
  searchTileDensity?: SearchTileDensity
  searchTileTreatment?: SearchTileTreatment
  oneClickBookingRestorationBanner?: React.ReactElement
  /**
   * Whether to prevent navigation when clicking on the tile
   * @default false
   */
  preventNavigationToOfferPage?: boolean
  storeOfferListTile?: boolean
}

interface OfferPricing {
  availability: boolean | undefined
  price: number | undefined
  roomRateId: string | undefined
  memberPrice: number | undefined
  duration: number | undefined
  extendedAvailability: App.OfferListMetaData['extendedAvailability'] | undefined
}

const tileStyleMap = {
  search: OfferSearchTile,
  frontpage: FrontPageTile,
  hero: HeroOfferTile,
}

function calculateExtendedWindowSize(extendedAvailability: App.OfferListMetaData['extendedAvailability'] | undefined): number | undefined {
  return (extendedAvailability?.checkOut && extendedAvailability?.checkIn) ?
      calculateDaysBetweenDates(extendedAvailability.checkOut, extendedAvailability.checkIn) :
    undefined
}

/**
 * Core offer list tile that will render the appropriate tile based on the type of item given
 */
const OfferListOfferTile = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    tileStyle,
    offerId,
    position,
    filters,
    viewPriceInPoints,
    eagerLoadImage,
    className,
    lazyLoad = 'none',
    wrapperClass,
    metaSearchResult,
    accommodationId,
    showPerNightPricing = false,
    searchTileDensity = 'default',
    searchTileTreatment = 'default',
    oneClickBookingRestorationBanner,
    preventNavigationToOfferPage = false,
    storeOfferListTile = false,
  } = props

  const currency = useContext(CurrencyContext)
  const [skipInView, setSkipInView] = useState(false)
  const [offerAvailability, setOfferAvailability] = useState<boolean | undefined>(undefined)
  const [offerPrice, setOfferPrice] = useState<number | undefined>(undefined)
  const [roomRateId, setRoomRateId] = useState<string | undefined>(undefined)
  const [offerMemberPrice, setOfferMemberPrice] = useState<number | undefined>(undefined)
  const [offerDuration, setOfferDuration] = useState<number | undefined>(undefined)
  const [offerExtendedAvailability, setOfferExtendedAvailability] = useState<App.OfferListMetaData['extendedAvailability'] | undefined>(undefined)
  const [pricingByOfferId, setPricingByOfferId] = useState<Record<string, OfferPricing>>({})
  const [pricingReadyByOfferId, setPricingReadyByOfferId] = useState<Record<string, boolean>>({})
  const [additionalImpressionOffers, setAdditionalImpressionOffers] = useState<Record<string, App.AnyOffer>>({})
  const trackedAdditionalImpressionOfferIds = useRef<Set<string>>(new Set())
  // Used to lazy load the tile if turned on
  const [tileInViewRef, tileInView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })
  const tileRef = useRef<HTMLDivElement>(null)
  const setTileRef = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    tileInViewRef(instance)
    tileRef.current = instance
  }, [tileInViewRef])

  useImperativeHandle(ref, () => tileRef.current!)

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, tileInView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  const Tile = tileStyleMap[tileStyle]
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [offer, , error] = useOffer(offerId, {
    requireSummaryOnly: true,
    disabled: lazyLoad !== 'none' && !hasBeenInView,
  })

  const showPackageUpgradeBanner = tileStyle === 'search' && getAccommodationSearchTileView(searchTileDensity).showUpgradeBanner
  const { fetching: fetchingLoyaltyPackageUpgrade } = useSearchPropertyRateLoyaltyPackageUpgrade(offer, {
    checkIn: filters?.checkIn,
    checkOut: filters?.checkOut,
    rooms: filters?.rooms,
    enabled: showPackageUpgradeBanner && hasBeenInView,
  })

  const [offerAvailabilityReady, setOfferAvailabilityReady] = useState(false)
  const [offerPricingReady, setOfferPricingReady] = useState(false)

  const isHotelOffer = useMemo(() => !!offer && isHotel(offer), [offer])

  useEffect(() => {
    if (isHotelOffer) return

    // We do not want to wait for pricing when the offer is not a hotel offer
    setOfferPricingReady(true)
  }, [isHotelOffer])

  const handleMouseEnter = useCallback(() => {
    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productHover,
        offer,
        persist: true,
      })
    }
  }, [dispatchOfferListEvent, offer])

  const handleMouseLeave = useCallback(() => {
    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productHover,
        offer,
        persist: false,
      })
    }
  }, [dispatchOfferListEvent, offer])

  const onProductClick = useCallback((clickedOffer = offer, isRightClick = false) => {
    if (!isRightClick && isLEOffer(clickedOffer)) {
      // TODO: remove this once G4A is up?
      // we only know how to register le offer clicks at the moment
      addGTMEvent(offerClicked(clickedOffer, currency, position))
    } else if (!isRightClick && isCruiseOffer(clickedOffer)) {
      fireInteractionEvent(cruiseOfferTileClick(clickedOffer.id))
    }

    if (clickedOffer) {
      const clickPricing = pricingByOfferId[clickedOffer.id] ?? (offer ? pricingByOfferId[offer.id] : undefined)

      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: clickedOffer,
        position,
        duration: clickPricing ? clickPricing.duration : offerDuration,
        price: clickPricing ? clickPricing.price : offerPrice,
        memberPrice: clickPricing ? clickPricing.memberPrice : offerMemberPrice,
        availability: clickPricing ? clickPricing.availability : offerAvailability,
        extendedAvailability: calculateExtendedWindowSize(clickPricing ? clickPricing.extendedAvailability : offerExtendedAvailability),
        isRightClick,
      })
    }
  }, [offer, currency, position, pricingByOfferId, dispatchOfferListEvent, offerDuration, offerPrice, offerMemberPrice, offerAvailability, offerExtendedAvailability])

  const handleAdditionalProductImpressionChange = useCallback((impressionOffer?: App.AnyOffer) => {
    setAdditionalImpressionOffers((additionalImpressionOffers) => {
      if (!impressionOffer || impressionOffer.id === offer?.id) {
        return Object.keys(additionalImpressionOffers).length > 0 ? {} : additionalImpressionOffers
      }

      if (additionalImpressionOffers[impressionOffer.id]) {
        return additionalImpressionOffers
      }

      return {
        [impressionOffer.id]: impressionOffer,
      }
    })
  }, [offer?.id])

  const dispatchProductImpression = useCallback((impressionOffer: App.AnyOffer, usePrimaryPricingFallback = false) => {
    let impressionPricing = pricingReadyByOfferId[impressionOffer.id] ? pricingByOfferId[impressionOffer.id] : undefined

    if (!impressionPricing && usePrimaryPricingFallback && offer) {
      impressionPricing = pricingByOfferId[offer.id]
    }

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: impressionOffer,
      position,
      availability: impressionPricing ? impressionPricing.availability : offerAvailability,
      key: EventDataKey.ProductImpression,
      duration: impressionPricing ? impressionPricing.duration : offerDuration,
      price: impressionPricing ? impressionPricing.price : offerPrice,
      memberPrice: impressionPricing ? impressionPricing.memberPrice : offerMemberPrice,
      roomRateId: impressionPricing ? impressionPricing.roomRateId : roomRateId,
      extendedAvailability: calculateExtendedWindowSize(impressionPricing ? impressionPricing.extendedAvailability : offerExtendedAvailability),
    })
  }, [
    pricingByOfferId,
    pricingReadyByOfferId,
    offer,
    dispatchOfferListEvent,
    position,
    offerAvailability,
    offerDuration,
    offerPrice,
    offerMemberPrice,
    roomRateId,
    offerExtendedAvailability,
  ])

  useEffect(() => {
    if (trackingInView && offer && offerAvailabilityReady && offerPricingReady) {
      dispatchProductImpression(offer, true)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView, offer, offerAvailabilityReady, offerPricingReady])

  useEffect(() => {
    if (!trackingInView || !offerAvailabilityReady || !offerPricingReady) {
      return
    }

    Object.values(additionalImpressionOffers).forEach((additionalImpressionOffer) => {
      if (trackedAdditionalImpressionOfferIds.current.has(additionalImpressionOffer.id)) {
        return
      }

      trackedAdditionalImpressionOfferIds.current.add(additionalImpressionOffer.id)
      dispatchProductImpression(additionalImpressionOffer)
    })
  }, [
    additionalImpressionOffers,
    dispatchProductImpression,
    offerAvailabilityReady,
    offerPricingReady,
    trackingInView,
  ])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.offerReady) {
      setOfferAvailabilityReady(true)
    } else if (dispatchAction.type === OfferListEvents.pricingCalculated) {
      const pricingOfferId = dispatchAction.offerId
      if (pricingOfferId) {
        setPricingReadyByOfferId(pricingReadyByOfferId => ({
          ...pricingReadyByOfferId,
          [pricingOfferId]: true,
        }))
        setPricingByOfferId(pricingByOfferId => ({
          ...pricingByOfferId,
          [pricingOfferId]: {
            price: dispatchAction.price,
            roomRateId: dispatchAction.roomRateId,
            memberPrice: dispatchAction.memberPrice,
            duration: dispatchAction.duration,
            extendedAvailability: dispatchAction.extendedAvailability,
            availability: dispatchAction.availability,
          },
        }))
      }

      setOfferPrice(dispatchAction.price)
      setRoomRateId(dispatchAction.roomRateId)
      setOfferMemberPrice(dispatchAction.memberPrice)
      setOfferDuration(dispatchAction.duration)
      setOfferExtendedAvailability(dispatchAction.extendedAvailability)
      setOfferAvailability(dispatchAction.availability)

      setOfferPricingReady(true)
    }
    dispatchOfferListEvent(dispatchAction, additionalData)
  }, [dispatchOfferListEvent])

  if (error) {
    return null
  }

  const showTile = !!(lazyLoad === 'offer-and-inview' ? offer && hasBeenInView : offer) && !fetchingLoyaltyPackageUpgrade

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <LoaderPlayStateContext.Provider value={hasBeenInView ? 'running' : 'paused'}>
        <div
          ref={setTileRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={wrapperClass}
          data-testid="offer-tile"
          data-metasearch-result={metaSearchResult}
        >
          {!showTile && (
            <OfferListLoadingOfferTile
              className={className}
              offerId={offerId}
              tileStyle={tileStyle}
              filters={filters}
              offerPosition={position}
              searchTileDensity={searchTileDensity}
              searchTileTreatment={searchTileTreatment}
            />
          )}
          {showTile && (
            <OfferListEventsProvider onListEvent={onListEvent}>
              {oneClickBookingRestorationBanner}
              <Tile
                className={className}
                offer={offer!}
                eagerLoadFirstImage={eagerLoadImage}
                filters={filters}
                viewPriceInPoints={viewPriceInPoints}
                productClick={onProductClick}
                onAdditionalProductImpressionChange={handleAdditionalProductImpressionChange}
                metaSearchResult={metaSearchResult}
                accommodationId={accommodationId}
                showPerNightPricing={showPerNightPricing}
                searchTileDensity={searchTileDensity}
                searchTileTreatment={searchTileTreatment}
                preventNavigationToOfferPage={preventNavigationToOfferPage}
                storeOfferSearchTile={storeOfferListTile}
                position={position}
              />
            </OfferListEventsProvider>
          )}
        </div>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
})

OfferListOfferTile.displayName = 'OfferListOfferTile'

export default memo(OfferListOfferTile)
